import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { IPodcastEpisodeFields, isDocument } from '@estercare/ester-shared'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { useGetEntriesByContentType } from '../api/contentful/contentfulQueries'
import { ComponentLayout } from './layouts/ComponentLayout'
import { BlockItem } from './BlockItem'
import { PodcastButton } from './PodcastButton'
import { BodyLarge, BodySmallMedium, Title3 } from './styles/Typography'
import { theme } from '../theme/theme'
import { Link } from 'react-router-dom'
import { Arrow } from './icons'

const GYNPODDEN_IMAGE_URL =
  'https://images.ctfassets.net/hr4hxqu06b00/2jIufZP597dhmxg4cAiiSD/035709e24aed446a4dbf0eeea6e2cd07/EsterCare_BlickaStudio_-19__1_.jpg'

export const PodcastHighlightSection = () => {
  const { t } = useTranslation()
  const podcastEntries = useGetEntriesByContentType<IPodcastEpisodeFields>('podcastEpisode').data?.items

  const latestEpisode = podcastEntries
    ?.map((entry) => entry.fields)
    .sort((a, b) => (b.episodeNumber || 0) - (a.episodeNumber || 0))[0]

  if (!latestEpisode) return null

  const subtitle =
    latestEpisode.description && isDocument(latestEpisode.description)
      ? documentToPlainTextString(latestEpisode.description)
      : ''

  return (
    <ComponentLayout paddingBottom={0} paddingTop={theme.spacing.xxlarge}>
      <Layout>
        <ImageWrapper>
          <StyledImage src={GYNPODDEN_IMAGE_URL} alt="Gynpodden" />
        </ImageWrapper>
        <Content>
          <InnerContent>
            <TextBlock>
              <Title3>{t('employee.home.podcastTitle')}</Title3>
              <BodyLarge>{t('employee.home.podcastDescription')}</BodyLarge>
            </TextBlock>
            <BlockItem height={98}>
              <PodcastButton
                title={latestEpisode.title || ''}
                subtitle={subtitle}
                number={`${latestEpisode.episodeNumber || 0}`}
                href={`/portal/resource-center/podcasts/${latestEpisode.slug}`}
              />
            </BlockItem>
          </InnerContent>
          <StyledLink to="/portal/resource-center">
            <BodySmallMedium>{t('employee.home.allPodcastEpisodes')}</BodySmallMedium>
            <Arrow size={18} color={theme.color.plumMid} />
          </StyledLink>
        </Content>
      </Layout>
    </ComponentLayout>
  )
}

const Layout = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.jumbo}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex-direction: column;
    gap: ${theme.spacing.large}px;
  }
`

const ImageWrapper = styled.div`
  flex-shrink: 0;
  max-width: 440px;
  width: 100%;
  height: 320px;
  border-radius: 40px;
  overflow: hidden;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    width: 100%;
    height: 250px;
    border-radius: ${theme.spacing.mediumLarge}px;
  }
`

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  display: block;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing.mediumLarge}px;
  padding: ${theme.spacing.mediumLarge}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    padding: ${theme.spacing.mediumLarge}px 0;
    gap: ${theme.spacing.large}px;
  }
`

const InnerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.mediumLarge}px;
  max-width: 562px;
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
  text-align: center;
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 2px;

  color: ${theme.color.plumMid};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    margin: auto;
  }
`
